import { appDevelopingModalPopupState } from "@/atoms/appPopupState"
import { networkAtom } from "@/atoms/settings"
import { SelectedReserveType } from "@/atoms/shared"
import { getLinkExplorerAddress } from "@/components/dashboard/commons"
import { useFetchNftWallet } from "@/hooks/useFetchNFT"
import { useAtom, useAtomValue } from "jotai"
import { ChangeEvent, Dispatch, FC, SetStateAction, useCallback, useMemo, useState } from "react"

import LinkIcon from "@/assets/icons/link.svg"
import NFTIcon from "@/assets/icons/nft.svg"
import TrashIcon from "@/assets/icons/trash.svg"
import useTrans from "@/hooks/useTrans"
import useWalletConnect from "@/hooks/useWalletConnect"
import Box from "@mui/material/Box"
import Chip from "@mui/material/Chip"
import IconButton from "@mui/material/IconButton"
import Link from "@mui/material/Link"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import Image from "next/image"
import { NFTVoucherType, SelectedNFTType } from "../borrowRepayModal/view"
import BorrowRepayDialog from "./components/BorrowRepayDialog"
import NFTDetailDialog from "./components/NFTDetailDialog"
import * as styles from "./styles"

interface RelendNFTProps {
  selectedReserve: SelectedReserveType
  selectedNFT?: SelectedNFTType
  setSelectedNFT: Dispatch<SetStateAction<SelectedNFTType>>
  shouldRefreshCheckbox: boolean
  refreshNFTCheckbox: Dispatch<SetStateAction<boolean>>
  error?: string
  currentTab: "borrow" | "repay"
}

const VOUCHER_INFO_INDEX = {
  repay: 0,
  borrow: 1,
}

const View: FC<RelendNFTProps> = ({
  error,
  selectedReserve,
  selectedNFT,
  setSelectedNFT,
  shouldRefreshCheckbox,
  refreshNFTCheckbox,
  currentTab,
}) => {
  const trans = useTrans()
  const { wallet } = useWalletConnect()
  const [searchText, setSearchText] = useState<string>()
  const [nftMintSelected, setNftMintSelected] = useState<string>()
  const [isOpenDetail, setIsOpenDetail] = useState(false)
  const [popupState, setPopupState] = useAtom(appDevelopingModalPopupState)
  const network = useAtomValue(networkAtom)

  const { data: dataNfts, refetch: refetchLendingNFT } = useFetchNftWallet(
    selectedReserve.mintAddress,
    wallet?.adapter.publicKey || null,
  )
  const selectedNFTKey = useMemo(() => {
    return currentTab === "borrow" ? NFTVoucherType.BORROW : NFTVoucherType.REPAY
  }, [currentTab])

  const availableLendingNFT = useMemo(() => {
    if (!dataNfts) return []

    const text = (searchText ?? "").toLowerCase()

    return dataNfts.filter((nft) => {
      const isValidVoucherForCurrentTab =
        (!nft.detail?.voucherInfo && currentTab === "repay") ||
        nft?.detail?.voucherInfo === VOUCHER_INFO_INDEX[currentTab]
      const matchesSearchCriteria =
        nft?.name.toLowerCase().includes(text) || nft.mintAddress.includes(text) || nft.mintAddress === searchText
      return isValidVoucherForCurrentTab && matchesSearchCriteria
    })
  }, [currentTab, dataNfts, searchText])

  const handleOpenModal = useCallback(() => {
    setPopupState({ isOpen: true })
  }, [setPopupState])

  const handleCloseModal = useCallback(() => {
    refetchLendingNFT()
    setPopupState({ isOpen: false })
  }, [setPopupState, refetchLendingNFT])

  const handleRemoveSelected = useCallback(() => {
    setSelectedNFT((prev) => ({
      ...prev,
      [selectedNFTKey]: null,
    }))
    refreshNFTCheckbox((prev) => !prev)
  }, [refreshNFTCheckbox, selectedNFTKey, setSelectedNFT])

  const handleSelectNFTChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setNftMintSelected((event.target as HTMLInputElement).value)
  }, [])

  const handleChooseNFTClick = useCallback(() => {
    if (nftMintSelected) {
      const nftSelected = availableLendingNFT.find((x) => x.mintAddress === nftMintSelected)
      setSelectedNFT((prev) => ({
        ...prev,
        [selectedNFTKey]: nftSelected,
      }))
    }

    handleCloseModal()
  }, [nftMintSelected, handleCloseModal, availableLendingNFT, setSelectedNFT, selectedNFTKey])

  const handleSearchText = useCallback((text: string) => {
    setSearchText(text)
  }, [])

  const handleOpenNFTDetailModal = useCallback(() => {
    setIsOpenDetail(true)
  }, [])
  const handleCloseNFTDetailModal = useCallback(() => {
    setIsOpenDetail(false)
  }, [])

  const selectedNFTForRender = selectedNFT?.[selectedNFTKey]

  return (
    <>
      <Stack sx={styles.containerModalNFT} gap={1} data-testid='box-select-nft'>
        <Stack direction='row' alignItems='center' justifyContent='space-between'>
          <Typography fontSize={{ xs: 13, sm: 14 }}>{trans.dashboard.modal.choose_nft_for_promotion}</Typography>
          {selectedNFTForRender && (
            <Typography
              data-testid='button-change-nft'
              sx={styles.typoChangeNFT}
              fontSize={{ xs: 13, sm: 14 }}
              onClick={handleOpenModal}
            >
              {trans.dashboard.modal.change_nft}
            </Typography>
          )}
        </Stack>
        {!selectedNFTForRender && (
          <Stack
            data-testid='box-empty-select-nft'
            sx={styles.boxSelectNFT}
            onClick={handleOpenModal}
            direction='row'
            alignItems='center'
            justifyContent='center'
          >
            <Image src={NFTIcon} alt='staking-icon' width={20} height={20} />
            <Typography sx={styles.typoSelectNFT}>{trans.dashboard.modal.select_nft}</Typography>
          </Stack>
        )}

        {selectedNFTForRender && (
          <>
            <Stack direction='row' alignItems='center' justifyContent='space-between' gap='4px'>
              <Box sx={styles.boxNftSelectedItem(!!error)}>
                {selectedNFTForRender?.detail && (
                  <Chip
                    sx={styles.boxNftItemDiscount}
                    label={`${selectedNFT[selectedNFTKey]?.detail?.discountPercentage}%`}
                  />
                )}
                <Image
                  src={selectedNFT[selectedNFTKey]?.imageUrl ?? NFTIcon}
                  alt='staking-icon'
                  width={82}
                  height={82}
                />
              </Box>
              <Stack flex='1' gap='4px'>
                <Typography
                  sx={{ ...styles.boxNftItemDetailTitle, fontSize: { xs: 12, sm: 14 } }}
                  className='underline cursor-pointer'
                  onClick={handleOpenNFTDetailModal}
                >
                  {selectedNFT[selectedNFTKey]?.name}
                </Typography>
                <Link
                  href={getLinkExplorerAddress(network, selectedNFT[selectedNFTKey]?.mintAddress ?? "")}
                  target='_blank'
                  sx={{ ...styles.boxNftItemDetailSubTitle, fontSize: { xs: 12, sm: 14 } }}
                >
                  <Image src={LinkIcon} width={12} height={12} alt='nft-image' />
                  <span>{selectedNFT[selectedNFTKey]?.displayMintAddress}</span>
                </Link>
                <Typography sx={{ ...styles.boxNftItemDetailPromotion, fontSize: { xs: 12, sm: 14 } }}>
                  {selectedNFT[selectedNFTKey]?.detail &&
                    trans.dashboard.modal.nft_promotion(
                      selectedNFT[selectedNFTKey]?.detail?.discountPercentage?.toNumber() ?? 0,
                      selectedNFT[selectedNFTKey]?.detail?.maximumAmount?.toNumber() ?? 0,
                    )}
                </Typography>
              </Stack>
              <IconButton data-testid='button-remove-nft' aria-label='trash' onClick={handleRemoveSelected}>
                <Image src={TrashIcon} alt='staking-icon' width={24} height={24} />
              </IconButton>
            </Stack>
            {error && <Typography sx={styles.typoError}>{error}</Typography>}
          </>
        )}
      </Stack>

      <BorrowRepayDialog
        isOpen={popupState.isOpen}
        handleCloseModal={handleCloseModal}
        availableLendingNFT={availableLendingNFT}
        title={trans.dashboard.modal.choose_nft_to_send}
        handleSearchText={handleSearchText}
        nftMintSelected={nftMintSelected}
        handleSelectNFTChange={handleSelectNFTChange}
        handleChooseNFTClick={handleChooseNFTClick}
        setNftMintSelected={setNftMintSelected}
        selectedReserve={selectedReserve}
        shouldRefreshCheckbox={shouldRefreshCheckbox}
      />

      {selectedNFTForRender && isOpenDetail && (
        <NFTDetailDialog
          isOpen={isOpenDetail}
          handleCloseModal={handleCloseNFTDetailModal}
          selectedNFT={selectedNFTForRender}
        />
      )}
    </>
  )
}

export default View
